import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Phone, RefreshCw, Package, Euro, Clock, ImagePlus, Trash2, X, Pencil } from "lucide-react";
import { API } from "@/shop/ShopContext";
import { CATEGORIES, VEHICLES } from "@/lib/catalog";
import { Reveal } from "@/components/Reveal";

const STATUSES = [
  { id: "in_attesa_pagamento", label: "In attesa pagamento" },
  { id: "in_attesa_bonifico", label: "In attesa bonifico" },
  { id: "confermato", label: "Confermato" },
  { id: "spedito", label: "Spedito" },
  { id: "consegnato", label: "Consegnato" },
  { id: "annullato", label: "Annullato" },
];

const STATUS_STYLE = {
  in_attesa_pagamento: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  in_attesa_bonifico: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  confermato: "border-sky-500/40 bg-sky-500/10 text-sky-400",
  spedito: "border-violet-500/40 bg-violet-500/10 text-violet-400",
  consegnato: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  annullato: "border-red-500/40 bg-red-500/10 text-red-400",
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#1C2028] px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-500 focus:border-[#FF5500] focus:outline-none";

const EMPTY_FORM = { name: "", code: "", category: "freni", price: "", stock: "1", description: "", imageUrl: "" };

const ProductsTab = ({ headers }) => {
  const [products, setProducts] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [vehicles, setVehicles] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editStock, setEditStock] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState("");
  const fileInput = useRef(null);
  const editFileInput = useRef(null);

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditName(p.name);
    setEditDesc(p.description || "");
    setEditPrice(String(p.price));
    setEditStock(String(p.stock));
    setEditImageUrl("");
    setEditFile(null);
    setEditPreview(p.image);
  };

  const pickEditFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setEditFile(f);
    setEditPreview(URL.createObjectURL(f));
  };

  const saveEdit = async (pid) => {
    try {
      const body = {
        name: editName,
        description: editDesc,
        price: parseFloat(editPrice),
        stock: parseInt(editStock || "0", 10),
      };
      if (editFile) {
        const fd = new FormData();
        fd.append("file", editFile);
        const up = await axios.post(`${API}/admin/upload`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        body.image = up.data.url;
      } else if (editImageUrl) {
        body.image = editImageUrl;
      }
      const res = await axios.patch(`${API}/admin/products/${pid}`, body, { headers });
      setProducts((prev) => prev.map((x) => (x.id === pid ? { ...x, ...res.data } : x)));
      setEditingId(null);
      setEditFile(null);
      toast.success("Ricambio aggiornato");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Aggiornamento non riuscito");
    }
  };

  const load = () =>
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleVehicle = (v) =>
    setVehicles((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearFile = () => {
    setFile(null);
    setPreview("");
    if (fileInput.current) fileInput.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code || !form.price) {
      toast.error("Compila nome, codice e prezzo");
      return;
    }
    if (!file && !form.imageUrl) {
      toast.error("Carica una foto del ricambio o incolla un link immagine");
      return;
    }
    setSaving(true);
    try {
      let image = form.imageUrl;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/admin/upload`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        image = up.data.url;
      }
      await axios.post(
        `${API}/admin/products`,
        {
          name: form.name,
          code: form.code,
          category: form.category,
          vehicles,
          price: parseFloat(form.price),
          stock: parseInt(form.stock || "1", 10),
          description: form.description,
          image,
        },
        { headers }
      );
      toast.success("Ricambio aggiunto al catalogo");
      setForm(EMPTY_FORM);
      setVehicles([]);
      clearFile();
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}" dal catalogo?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers });
      toast.success("Ricambio eliminato");
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-5" data-testid="products-tab">
      <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#14171D] p-6 lg:col-span-2" data-testid="product-form">
        <h2 className="font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
          Nuovo ricambio
        </h2>
        <div className="mt-5 space-y-4">
          <input data-testid="pf-name" className={inputCls} placeholder="Nome ricambio * (es. Pastiglie Freno Honda SH)" value={form.name} onChange={set("name")} />
          <div className="grid grid-cols-2 gap-4">
            <input data-testid="pf-code" className={inputCls} placeholder="Codice OEM *" value={form.code} onChange={set("code")} />
            <select data-testid="pf-category" className={inputCls} value={form.category} onChange={set("category")}>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input data-testid="pf-price" type="number" step="0.01" min="0" className={inputCls} placeholder="Prezzo € *" value={form.price} onChange={set("price")} />
            <input data-testid="pf-stock" type="number" min="0" className={inputCls} placeholder="Giacenza" value={form.stock} onChange={set("stock")} />
          </div>
          <textarea data-testid="pf-description" rows={2} className={inputCls} placeholder="Descrizione breve" value={form.description} onChange={set("description")} />

          <div>
            <p className="mb-2 text-[11px] uppercase tracking-widest text-neutral-500">Compatibilità veicoli</p>
            <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto rounded-lg border border-white/10 bg-[#0C0E12] p-3" data-testid="pf-vehicles">
              {VEHICLES.map((v) => (
                <button
                  type="button"
                  key={v}
                  onClick={() => toggleVehicle(v)}
                  data-testid={`pf-vehicle-${v.replace(/[\s/]+/g, "-").toLowerCase()}`}
                  className={`rounded-full px-3 py-1 text-[11px] transition-colors ${
                    vehicles.includes(v)
                      ? "bg-[#FF5500] text-white"
                      : "border border-white/15 text-neutral-400 hover:border-[#FF5500]"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] uppercase tracking-widest text-neutral-500">Foto ricambio *</p>
            {preview ? (
              <div className="relative inline-block">
                <img src={preview} alt="Anteprima" className="h-28 w-28 rounded-lg border border-white/15 object-cover" data-testid="pf-preview" />
                <button type="button" onClick={clearFile} className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white" data-testid="pf-clear-photo" aria-label="Rimuovi foto">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                data-testid="pf-upload-btn"
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 bg-[#0C0E12] py-6 text-sm text-neutral-400 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
              >
                <ImagePlus className="h-5 w-5" /> Carica foto dal telefono o computer
              </button>
            )}
            <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="pf-file-input" />
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-widest text-neutral-600">oppure</span>
              <input data-testid="pf-image-url" className={inputCls} placeholder="Incolla link immagine (https://...)" value={form.imageUrl} onChange={set("imageUrl")} />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="pf-submit"
            className="w-full rounded-lg bg-[#FF5500] py-3.5 font-display text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00] disabled:opacity-60"
          >
            {saving ? "Salvataggio..." : "Aggiungi al catalogo"}
          </button>
        </div>
      </form>

      <div className="lg:col-span-3">
        <h2 className="font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
          Catalogo attuale {products && <span className="text-neutral-500">({products.length})</span>}
        </h2>
        {products === null ? (
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-xl border border-white/10 bg-[#14171D]" />
            ))}
          </div>
        ) : (
          <div className="mt-5 grid max-h-[70vh] gap-3 overflow-y-auto pr-1 sm:grid-cols-2" data-testid="admin-products-list">
            {products.map((p) =>
              editingId === p.id ? (
                <div key={p.id} className="rounded-xl border border-[#FF5500]/50 bg-[#14171D] p-4 sm:col-span-2" data-testid={`admin-product-${p.id}`}>
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <img src={editPreview} alt={p.name} className="h-20 w-20 rounded-lg border border-white/15 object-cover" data-testid={`edit-preview-${p.id}`} />
                      <button
                        type="button"
                        onClick={() => editFileInput.current?.click()}
                        className="mt-2 flex w-20 items-center justify-center gap-1 rounded-md border border-dashed border-white/20 py-1.5 text-[10px] uppercase tracking-wide text-neutral-400 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
                        data-testid={`edit-photo-btn-${p.id}`}
                      >
                        <ImagePlus className="h-3 w-3" /> Foto
                      </button>
                      <input ref={editFileInput} type="file" accept="image/*" className="hidden" onChange={pickEditFile} data-testid={`edit-file-${p.id}`} />
                    </div>
                    <div className="min-w-[220px] flex-1 space-y-2">
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full rounded-md border border-white/15 bg-[#0C0E12] px-3 py-2 text-xs text-neutral-100 focus:border-[#FF5500] focus:outline-none"
                        placeholder="Nome ricambio"
                        data-testid={`edit-name-${p.id}`}
                      />
                      <input
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        className="w-full rounded-md border border-white/15 bg-[#0C0E12] px-3 py-2 text-xs text-neutral-100 focus:border-[#FF5500] focus:outline-none"
                        placeholder="Descrizione"
                        data-testid={`edit-desc-${p.id}`}
                      />
                      <input
                        value={editImageUrl}
                        onChange={(e) => setEditImageUrl(e.target.value)}
                        className="w-full rounded-md border border-white/15 bg-[#0C0E12] px-3 py-2 text-xs text-neutral-100 focus:border-[#FF5500] focus:outline-none"
                        placeholder="...oppure incolla un link immagine (https://...)"
                        data-testid={`edit-image-url-${p.id}`}
                      />
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editPrice}
                          onChange={(e) => setEditPrice(e.target.value)}
                          className="w-24 rounded-md border border-white/15 bg-[#0C0E12] px-2 py-2 text-xs text-neutral-100 focus:border-[#FF5500] focus:outline-none"
                          data-testid={`edit-price-${p.id}`}
                          placeholder="€ prezzo"
                        />
                        <input
                          type="number"
                          min="0"
                          value={editStock}
                          onChange={(e) => setEditStock(e.target.value)}
                          className="w-20 rounded-md border border-white/15 bg-[#0C0E12] px-2 py-2 text-xs text-neutral-100 focus:border-[#FF5500] focus:outline-none"
                          data-testid={`edit-stock-${p.id}`}
                          placeholder="pz"
                        />
                        <button onClick={() => saveEdit(p.id)} className="rounded-md bg-[#FF5500] px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-white" data-testid={`save-edit-${p.id}`}>
                          Salva
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setEditFile(null);
                          }}
                          className="rounded-md border border-white/15 px-4 py-2 text-[11px] uppercase tracking-wide text-neutral-400"
                          data-testid={`cancel-edit-${p.id}`}
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={p.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#14171D] p-3" data-testid={`admin-product-${p.id}`}>
                  <img src={p.image} alt={p.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-neutral-100">{p.name}</p>
                    <p className="font-mono text-[11px] text-[#FF5500]">{p.code}</p>
                    <p className="text-xs text-neutral-400">
                      € {p.price.toFixed(2)} · giacenza {p.stock}
                    </p>
                  </div>
                  <button onClick={() => startEdit(p)} className="rounded-md border border-white/10 p-2 text-neutral-500 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]" data-testid={`edit-product-${p.id}`} aria-label="Modifica">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(p)} className="rounded-md border border-white/10 p-2 text-neutral-500 transition-colors hover:border-red-400 hover:text-red-400" data-testid={`delete-product-${p.id}`} aria-label="Elimina">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem("dr_admin") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("ordini");

  const headers = { Authorization: `Bearer ${token}` };

  const load = async (t = token) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${t}` } });
      setOrders(res.data);
    } catch {
      setToken("");
      localStorage.removeItem("dr_admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("dr_admin", res.data.token);
      setToken(res.data.token);
      toast.success("Accesso effettuato");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Accesso non riuscito");
    }
  };

  const logout = () => {
    localStorage.removeItem("dr_admin");
    setToken("");
    setOrders(null);
  };

  const setStatus = async (ref, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${ref}/status`, { status }, { headers });
      setOrders((prev) => prev.map((o) => (o.ref === ref ? { ...o, status } : o)));
      toast.success(`Ordine ${ref} aggiornato`);
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  if (!token) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24" data-testid="admin-login-page">
        <Reveal className="w-full">
          <div className="w-full rounded-2xl border border-white/10 bg-[#14171D] p-8">
            <Lock className="mx-auto h-10 w-10 text-[#FF5500]" />
            <h1 className="mt-4 text-center font-display text-2xl font-bold uppercase tracking-wide text-neutral-100">
              Area ordini negozio
            </h1>
            <p className="mt-2 text-center text-sm text-neutral-400">Accesso riservato a Delivery Ricambi.</p>
            <form onSubmit={login} className="mt-6 space-y-4">
              <input
                data-testid="admin-email-input"
                type="email"
                className={inputCls}
                placeholder="Email admin"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <input
                data-testid="admin-password-input"
                type="password"
                className={inputCls}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {error && (
                <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" data-testid="admin-login-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-btn"
                className="w-full rounded-lg bg-[#FF5500] py-3.5 font-display text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00]"
              >
                Accedi
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    );
  }

  const totalValue = (orders || []).filter((o) => o.status !== "annullato").reduce((s, o) => s + o.total, 0);
  const pending = (orders || []).filter((o) => ["in_attesa_pagamento", "in_attesa_bonifico"].includes(o.status)).length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-14" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-display text-xs uppercase tracking-[0.4em] text-[#FF5500]">Area riservata</p>
          <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight text-neutral-100">
            Gestione negozio
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => load()}
            data-testid="admin-refresh-btn"
            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-display text-xs uppercase tracking-[0.15em] text-neutral-200 transition-colors hover:border-[#FF5500]"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-display text-xs uppercase tracking-[0.15em] text-neutral-200 transition-colors hover:border-red-400 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </div>

      <div className="mt-8 flex gap-2">
        {[
          { id: "ordini", label: "Ordini" },
          { id: "prodotti", label: "Prodotti" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`rounded-full px-6 py-2.5 font-display text-sm uppercase tracking-[0.2em] transition-colors ${
              tab === t.id ? "bg-[#FF5500] text-white" : "border border-white/15 text-neutral-300 hover:border-[#FF5500]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "prodotti" ? (
        <ProductsTab headers={headers} />
      ) : (
        <>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Package, label: "Ordini totali", value: orders?.length ?? "—", testid: "stat-total" },
              { icon: Clock, label: "Da evadere", value: orders ? pending : "—", testid: "stat-pending" },
              { icon: Euro, label: "Valore ordini", value: orders ? `€ ${totalValue.toFixed(2)}` : "—", testid: "stat-value" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-white/10 bg-[#14171D] p-5" data-testid={s.testid}>
                <s.icon className="h-5 w-5 text-[#FF5500]" />
                <p className="mt-3 font-display text-3xl text-neutral-100">{s.value}</p>
                <p className="text-xs uppercase tracking-widest text-neutral-500">{s.label}</p>
              </div>
            ))}
          </div>

          {orders === null ? (
            <div className="mt-8 space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-32 animate-pulse rounded-xl border border-white/10 bg-[#14171D]" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="mt-8 rounded-xl border border-white/10 bg-[#14171D] py-16 text-center text-neutral-400" data-testid="admin-no-orders">
              Nessun ordine ricevuto finora.
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              {orders.map((o) => (
                <div key={o.ref} className="rounded-xl border border-white/10 bg-[#14171D] p-5" data-testid={`admin-order-${o.ref}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-mono text-lg font-semibold text-[#FF5500]">{o.ref}</span>
                        <span className={`rounded-full border px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLE[o.status] || "border-white/20 text-neutral-300"}`} data-testid={`status-badge-${o.ref}`}>
                          {STATUSES.find((s) => s.id === o.status)?.label || o.status}
                        </span>
                        <span className="rounded-full border border-white/15 px-3 py-0.5 text-[11px] uppercase tracking-wider text-neutral-400">
                          {o.payment_method === "card" ? "Carta" : "Bonifico"}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-neutral-500">
                        {new Date(o.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-2xl text-neutral-100">€ {o.total.toFixed(2)}</span>
                      <select
                        value={o.status}
                        onChange={(e) => setStatus(o.ref, e.target.value)}
                        data-testid={`status-select-${o.ref}`}
                        className="rounded-lg border border-white/15 bg-[#1C2028] px-3 py-2 text-xs text-neutral-100 focus:border-[#FF5500] focus:outline-none"
                      >
                        {STATUSES.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-4 border-t border-white/5 pt-4 md:grid-cols-2">
                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-neutral-500">Cliente</p>
                      <p className="mt-1 text-sm font-semibold text-neutral-100">{o.customer.name}</p>
                      <a href={`tel:${o.customer.phone}`} className="mt-0.5 flex items-center gap-1.5 text-sm text-[#FF5500]" data-testid={`customer-phone-${o.ref}`}>
                        <Phone className="h-3.5 w-3.5" /> {o.customer.phone}
                      </a>
                      {o.customer.email && <p className="text-sm text-neutral-400">{o.customer.email}</p>}
                      <p className="mt-1 text-sm text-neutral-400">
                        {o.customer.address}, {o.customer.city} {o.customer.cap}
                      </p>
                      {o.customer.note && <p className="mt-1 text-xs italic text-neutral-500">Nota: {o.customer.note}</p>}
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-neutral-500">Ricambi ordinati</p>
                      <ul className="mt-1 space-y-1 text-sm text-neutral-300">
                        {o.items.map((i) => (
                          <li key={i.product_id} className="flex justify-between gap-3">
                            <span>
                              {i.qty} × {i.name} <span className="font-mono text-[11px] text-neutral-500">{i.code}</span>
                            </span>
                            <span className="font-mono text-neutral-400">€ {i.line_total.toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Admin;
